import {
	SearchEngine,
	SearchBox as HeadlessSearchBox,
	buildSearchBox,
	ResultList as HeadlessResultList,
	buildResultList,
	buildFacet,
	buildResultsPerPage,
	buildPager,
	buildInstantResults,
	SearchAppState,
	StandaloneSearchBoxAnalytics,
	loadSearchAnalyticsActions,
	loadQueryActions,
	Unsubscribe,
} from '@coveo/headless';
import { Component } from 'react';
import { SearchBox } from './utils/search-box.fn';
import { ResultList } from './utils/result-list.fn';
import { Context } from './utils/context';
import { standaloneSearchBoxStorageKey } from './utils/standalone-search-box-storage-key';
import { bindExecuteTrigger } from './utils/execute-trigger';
import { bindUrlManager } from './utils/url-manager';
import { AppContext } from './utils/engine';
import React from 'react';

declare global {
	interface Window {
		HEADLESS_STATE?: SearchAppState;
	}
}

const isServerSideRendered = globalThis.window?.HEADLESS_STATE;

export interface SearchPageProps {
	engine?: SearchEngine;
}

interface ParentProps {
	results_layout?: string;
	results_columns?: string;
	results_per_page?: string;
}

export class StandAloneSearchPage extends Component {
	private engine: SearchEngine;
	private parentProps: ParentProps;

	private readonly searchBox: HeadlessSearchBox;

	private readonly resultList: HeadlessResultList;

	private unsubscribeUrlManager!: Unsubscribe;

	private unsubscribeExecuteTrigger!: Unsubscribe;

	constructor( props ) {
		super( props );

		this.initEngine( props );
		this.parentProps = props;

		const fieldsToInclude = [
			'objecttype',
			'filetype',
			'author',
			'date',
			'documenttype',
			'imagepreview',
		];

		this.searchBox = buildSearchBox( this.engine, {
			options: { numberOfSuggestions: 8 },
		} );

		this.resultList = buildResultList( this.engine, {
			options: {
				fieldsToInclude,
			},
		} );

		buildResultsPerPage( this.engine, {
			initialState: { numberOfResults: 10 },
		} );

		buildPager( this.engine, { options: { numberOfPages: 6 } } );
	}

	initEngine( props ) {
		if ( props.engine ) {
			this.engine = props.engine;
			return;
		}
	}

	componentDidMount() {
		// Search parameters, defined in the url's hash, are restored once `UrlManager` is registered.
		// Beware not to restore search parameters until all components are registered,
		// otherwise some components such as facets may not work when the page loads.
		// In this sample, `SearchPage.componentDidMount` happens to be executed after
		// all components are registered, but depending on your implementation this may
		// not be your case.
		this.unsubscribeUrlManager = bindUrlManager( this.engine );

		this.unsubscribeExecuteTrigger = bindExecuteTrigger( this.engine );

		// A search should not be executed until the search parameters are restored.
		this.executeInitialSearch();
	}

	componentWillUnmount() {
		this.unsubscribeUrlManager();
		this.unsubscribeExecuteTrigger();
	}

	private executeInitialSearch() {
		if ( isServerSideRendered ) {
			const { logInterfaceLoad } = loadSearchAnalyticsActions( this.engine );
			this.engine.dispatch( logInterfaceLoad() );
			return;
		}

		const data = localStorage.getItem( standaloneSearchBoxStorageKey );

		if ( data ) {
			this.executeFirstSearchAfterStandaloneSearchBoxRedirect( data );
			return;
		}

		if ( this?.searchBox?.state?.value ) {
			this.engine.executeFirstSearch();
		}
	}

	private executeFirstSearchAfterStandaloneSearchBoxRedirect( data: string ) {
		localStorage.removeItem( standaloneSearchBoxStorageKey );
		const parsed: {
			value: string;
			analytics: StandaloneSearchBoxAnalytics;
		} = JSON.parse( data );
		const { value, analytics } = parsed;
		const { updateQuery: q } = loadQueryActions( this.engine );

		this.engine.dispatch( q( { q: value } ) );
		this.engine.executeFirstSearchAfterStandaloneSearchBoxRedirect( analytics );
	}

	render() {
		return (
			<div className="coveo-standalone-searchbox__wrap">
				<AppContext.Provider value={ { engine: this.engine } }>
					<Context />
					<SearchBox controller={ this.searchBox } />
					<div
						className={
							`coveo-standalone-searchbox__results` +
							( this.parentProps.results_layout ? ` coveo-standalone-searchbox--${ this.parentProps.results_layout }` : '' ) +
							( this.parentProps.results_layout === 'grid' ? ` coveo-standalone-searchbox__results--${ this.parentProps.results_columns }c` : '' )
						}
					>
						<ResultList controller={ this.resultList } />
					</div>
				</AppContext.Provider>
			</div>
		);
	}
}
